import React, { useMemo } from 'react';
import { styled, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { demographicSources, eventDemographics as demographicCategories } from '@lib/constants';
import EventDemographicsModal from '@components/ContentComponents/Events/Demographics/EventDemographicsModal';
import { getAgeGroups, getMaxSessionParticipants } from '@components/Pages/Events/eventFunctions';

const DataHeading = styled(Typography)({
  fontSize: '16px',
  lineHeight: '40px',
});

const DataTable = styled(Table)({
  width: 'auto',
  marginBottom: '24px',
});

const TableHeadCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  fontWeight: 'bold',
  height: '56px',
  padding: '4px 8px',
  textAlign: 'center',
});

const TableDataCell = styled(TableCell)({
  height: '56px',
  textAlign: 'center',
});

const EventDemographics = ({ event, showEdit }) => {
  const ageRanges = getAgeGroups(event);
  const theme = useTheme();

  const uniqueParticipants = useMemo(() => {
    if (!event) {
      return '';
    }
    return getMaxSessionParticipants(event);
  }, [event]);

  return (
    <>
      {showEdit && (
        <EventDemographicsModal event={event} />
      )}

      <Typography variant="body1" sx={{ fontFamily: 'Montserrat', marginTop: '38px', marginBottom: '24px' }} gutterBottom>
          <span style={{ fontWeight: 600, fontSize: theme.typography.pxToRem(22) }}>
            Total Participants for this event: {uniqueParticipants || '-'}
          </span>
      </Typography>

      {event?.demographics && (
        <>
          <DataHeading variant="h4">AGE GROUP</DataHeading>

          <DataTable data-cy="table-age">
            <TableHead>
              <TableRow>
                {ageRanges.map(ageRange => (
                  <TableHeadCell key={`th-${ageRange.id}`} sx={{ 'width': `${100 / ageRanges.length}%` }}>
                    {ageRange.label}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {ageRanges.map(ageRange => (
                  <TableDataCell key={`td-${ageRange.id}`}>
                    {event.demographics.age[ageRange.id]}
                  </TableDataCell>
                ))}
              </TableRow>
            </TableBody>
          </DataTable>

          <DataHeading variant="h4">GENDER</DataHeading>

          <DataTable data-cy="table-gender">
            <TableHead>
              <TableRow>
                {demographicCategories.gender.map(gender => (
                  <TableHeadCell key={`th-${gender.id}`} sx={{ width: '20%' }}>
                    {gender.label}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {demographicCategories.gender.map((gender) => (
                  <TableDataCell key={`td-${gender.id}`}>
                    {event.demographics.gender[gender.id]}
                  </TableDataCell>
                ))}
              </TableRow>
            </TableBody>
          </DataTable>

          <DataHeading variant="h4">RACE AND ETHNICITY</DataHeading>

          <DataTable data-cy="table-race" sx={{ maxWidth: '1250px' }}>
            <TableHead>
              <TableRow>
                {demographicCategories.raceEthnicity.map(race => (
                  <TableHeadCell sx={{
                    maxWidth: 180,
                    width: '12.5%',
                  }} key={`th-${race.id}`}>
                    {race.label}
                  </TableHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {demographicCategories.raceEthnicity.map(race => (
                  <TableDataCell key={`td-${race.id}`}>
                    {event.demographics.raceEthnicity[race.id]}
                  </TableDataCell>
                ))}
              </TableRow>
            </TableBody>
          </DataTable>

          <Box mt="28px">
            <Typography variant="body1" sx={{ fontWeight: 600 }}>Who did you get the demographics from?</Typography>
            <Typography variant="body1" sx={{
              // borderBottom: '1px solid rgba(224, 224, 224, 1)',
              mt: 2,
              mb: 2,
              maxWidth: '150px',
              minHeight: 20,
            }}>
              {demographicSources.find(opt => opt.id === event.demographics?.source)?.label}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};
export default EventDemographics;
